import { ActionModel, ChasableCustomerApiModel, ChasableInvoiceApiModel } from "../../../models/ChasableModels";
import type { DisputedFilterValue, DueDateFilterValue } from "./Filters/SuggestedChasableFiltersContext";

export type SuggestedChasableApiFilters = {
  disputedFilter: DisputedFilterValue;
  disputedReasonFilter: string[];
  dueDateFilter: DueDateFilterValue;
  currencyFilter: string[];
};

/**
 * Builds a map of API invoice id -> invoice for O(1) lookup.
 * Hub ActionModel.externalId maps to the API chasable invoice id.
 */
function getInvoiceByIdLookup(apiChasables: ChasableCustomerApiModel[]): Map<string, ChasableInvoiceApiModel> {
  const map = new Map<string, ChasableInvoiceApiModel>();
  for (const c of apiChasables) {
    for (const inv of c.invoices) {
      map.set(inv.id, inv);
    }
  }
  return map;
}

/**
 * Returns whether the given API invoice matches the dispute filter (inclusive):
 * - Invoice must be disputed (isDisputed === true).
 * - Invoice's reasonCode or disputeReason must be in disputedReasonFilter (caller ensures filter is non-empty).
 */
function invoiceMatchesDisputeFilter(inv: ChasableInvoiceApiModel, disputedReasonFilter: string[]): boolean {
  if (!inv.dispute) return false;
  const reasonCode = inv.dispute?.reasonCode ?? "";
  const disputeReason = inv.dispute.reason ?? "";
  return disputedReasonFilter.includes(reasonCode) || disputedReasonFilter.includes(disputeReason);
}

function invoiceMatchesDisputedFilter(inv: ChasableInvoiceApiModel, disputedFilter: DisputedFilterValue): boolean {
  if (disputedFilter === "all") return true;
  if (disputedFilter === "disputed") return !!inv.dispute;
  if (disputedFilter === "not_disputed") return !inv.dispute;
  return true;
}

function invoiceMatchesDueDateFilter(inv: ChasableInvoiceApiModel, dueDateFilter: DueDateFilterValue): boolean {
  if (dueDateFilter === "all") return true;
  const due = new Date(inv.dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  if (dueDateFilter === "overdue") return due < today;
  if (dueDateFilter === "7") {
    const in7 = new Date(today);
    in7.setDate(in7.getDate() + 7);
    return due >= today && due <= in7;
  }
  if (dueDateFilter === "30") {
    const in30 = new Date(today);
    in30.setDate(in30.getDate() + 30);
    return due >= today && due <= in30;
  }
  return true;
}

function invoiceMatchesCurrencyFilter(inv: ChasableInvoiceApiModel, currencyFilter: string[]): boolean {
  if (currencyFilter.length === 0) return true;
  return currencyFilter.includes(inv.currency);
}

/**
 * Filters hub actions by dispute status from the API.
 * - Matches hub actions to API invoices by ActionModel.externalId === invoice.id.
 * - When disputedReasonFilter is empty: no filter — show all (disputed and non-disputed).
 * - When disputedReasonFilter has values: show only receivables whose matching invoice is disputed and its reason is in the selected filter(s).
 */
export function filterHubActionsByApiDispute(
  hubActions: ActionModel[],
  apiChasables: ChasableCustomerApiModel[],
  disputedReasonFilter: string[]
): ActionModel[] {
  if (disputedReasonFilter.length === 0) return hubActions;

  const invoiceById = getInvoiceByIdLookup(apiChasables);

  return hubActions.filter((action) => {
    const invoice = invoiceById.get(action.externalId);
    if (!invoice) return false;

    return invoiceMatchesDisputeFilter(invoice, disputedReasonFilter);
  });
}

/**
 * Applies all suggested chasable filters (dispute, due date, currency) using API invoice data.
 * Hub actions are matched to API invoices by ActionModel.externalId === invoice.id.
 * - Dispute: empty = show all; with values = show only those dispute reasons.
 * - Due date / currency: empty or "all" = no filter; with values = include only matching.
 * - When no API invoice is found for an action, it is excluded if any filter that needs API data is set.
 */
export function filterHubActionsByApiFilters(
  hubActions: ActionModel[],
  apiChasables: ChasableCustomerApiModel[],
  filters: SuggestedChasableApiFilters
): ActionModel[] {
  const hasDisputedFilter = filters.disputedFilter !== "all";
  const hasDisputeReasonFilter = filters.disputedReasonFilter.length > 0;
  const hasDueDateFilter = filters.dueDateFilter !== "all";
  const hasCurrencyFilter = filters.currencyFilter.length > 0;
  const needsApiData = hasDisputedFilter || hasDisputeReasonFilter || hasDueDateFilter || hasCurrencyFilter;

  if (!needsApiData) return hubActions;

  const invoiceById = getInvoiceByIdLookup(apiChasables);

  return hubActions.filter((action) => {
    const invoice = invoiceById.get(action.externalId);
    if (!invoice) return false;

    if (hasDisputedFilter && !invoiceMatchesDisputedFilter(invoice, filters.disputedFilter)) return false;
    if (hasDisputeReasonFilter && !invoiceMatchesDisputeFilter(invoice, filters.disputedReasonFilter)) return false;
    if (hasDueDateFilter && !invoiceMatchesDueDateFilter(invoice, filters.dueDateFilter)) return false;
    if (hasCurrencyFilter && !invoiceMatchesCurrencyFilter(invoice, filters.currencyFilter)) return false;

    return true;
  });
}
